import { useCallback, useState } from 'react';

import { SetterOrUpdater } from 'recoil';

import { updateCouponRestrictedPhone } from '@shared/api/giftCoupon/giftCoupon.controller';
import { GiftCouponForStore } from '@shared/api/giftCoupon/giftCoupon.interface';
import { updateDeliveryRestrictedPhone } from '@shared/api/giftDelivery/giftDelivery.controller';
import { GiftDeliveryForStore } from '@shared/api/giftDelivery/giftDelivery.interface';
import { GiftOrderForStore, GiftStoreRole } from '@shared/api/giftOrder/giftOrder.interface';
import { toastError } from '@shared/plugIn/toast';
import { phoneNumberStringFormator } from '@shared/utils/phoneNumberStringFormator';

import { PendingConfirmItem } from './giftShareTypes';

interface UseGiftPhoneLockEditModalParams {
  role: GiftStoreRole;
  setOrderData: SetterOrUpdater<GiftOrderForStore | null>;
}

/**
 * 휴대폰번호 잠금 수정/추가 모달 (PhoneLockEditModal).
 * 잘못 보낸 케이스에서 회수 플로우로 잠긴 번호를 사후에 정정하거나, 공유된 답례품에
 * 예방 차원의 번호 잠금을 추가하기 위한 별도 진입점. (restricted-phone PATCH 재사용)
 *
 * useGiftOrderShare 에서 분리 — role, setOrderData 에만 의존하는 독립 모달.
 * (회수 플로우와 달리 unshare/공유 시트 재오픈은 하지 않는다 — 단순 갱신)
 */
export function useGiftPhoneLockEditModal({
  role,
  setOrderData,
}: UseGiftPhoneLockEditModalParams) {
  const [phoneLockTarget, setPhoneLockTarget] = useState<PendingConfirmItem | null>(null);
  const [showPhoneLockEdit, setShowPhoneLockEdit] = useState(false);
  const [isPhoneLockSaving, setIsPhoneLockSaving] = useState(false);

  const handleOpenPhoneLockEdit = useCallback(
    (item: GiftCouponForStore | GiftDeliveryForStore, kind: 'coupon' | 'delivery') => {
      // 수령자가 이미 번호 인증을 마쳤거나(verification.phoneNumber, 쿠폰 전용)
      // 교환/수령 신청이 일어난 경우(recipient.phoneNumber)에는 수신번호를
      // 잠그거나 변경할 수 없다. 마지막 4자리를 가려 안내만 제공.
      const verifiedPhone =
        kind === 'coupon'
          ? (item as GiftCouponForStore).verification?.phoneNumber
          : undefined;
      const requestedPhone = verifiedPhone || item.recipient?.phoneNumber;
      if (requestedPhone) {
        const digits = requestedPhone.replace(/[^0-9]/g, '');
        const masked =
          digits.length >= 4
            ? `${phoneNumberStringFormator(digits.slice(0, -4))}-****`
            : '****';
        window.alert(
          `이미 ${masked} (으)로 수령 신청이 완료되어 수신번호를 변경할 수 없습니다.`,
        );
        return;
      }
      setPhoneLockTarget(
        kind === 'coupon'
          ? { type: 'coupon', item: item as GiftCouponForStore }
          : { type: 'delivery', item: item as GiftDeliveryForStore },
      );
      setShowPhoneLockEdit(true);
    },
    [],
  );

  const handleClosePhoneLockEdit = useCallback(() => {
    if (isPhoneLockSaving) return;
    setShowPhoneLockEdit(false);
    setPhoneLockTarget(null);
  }, [isPhoneLockSaving]);

  const handlePhoneLockSave = useCallback(
    async (phoneNumber: string, memo?: string) => {
      if (!phoneLockTarget) return;
      setIsPhoneLockSaving(true);
      const trimmedMemo = memo?.trim() || undefined;
      try {
        if (phoneLockTarget.type === 'coupon') {
          const coupon = phoneLockTarget.item;
          await updateCouponRestrictedPhone(coupon._id, phoneNumber, role, trimmedMemo);
          setOrderData((prev) => {
            if (!prev || !prev.coupons) return prev;
            return {
              ...prev,
              coupons: prev.coupons.map((c) =>
                c._id === coupon._id
                  ? {
                      ...c,
                      additionalRecipientPhone: phoneNumber,
                      memo: trimmedMemo ?? c.memo,
                    }
                  : c,
              ),
            };
          });
        } else {
          const delivery = phoneLockTarget.item;
          await updateDeliveryRestrictedPhone(
            delivery._id,
            phoneNumber,
            role,
            trimmedMemo,
          );
          setOrderData((prev) => {
            if (!prev || !prev.deliveries) return prev;
            return {
              ...prev,
              deliveries: prev.deliveries.map((d) =>
                d._id === delivery._id
                  ? {
                      ...d,
                      additionalRecipientPhone: phoneNumber,
                      memo: trimmedMemo ?? d.memo,
                    }
                  : d,
              ),
            };
          });
        }
        setShowPhoneLockEdit(false);
        setPhoneLockTarget(null);
      } catch (error) {
        console.error('번호 잠금 수정 실패:', error);
        toastError('번호 저장에 실패했어요. 잠시 후 다시 시도해 주세요.');
      } finally {
        setIsPhoneLockSaving(false);
      }
    },
    [phoneLockTarget, role, setOrderData],
  );

  return {
    showPhoneLockEdit,
    isPhoneLockSaving,
    phoneLockTarget,
    handleOpenPhoneLockEdit,
    handleClosePhoneLockEdit,
    handlePhoneLockSave,
  };
}
